"use client";

/**
 * @file 主题上下文组件
 * @description 提供全局主题管理功能，包括暗黑模式切换和保存用户偏好
 * 使用"use client"指令标记为客户端组件，因为需要访问DOM和浏览器API
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * 主题类型定义
 * @description 定义可用的主题选项
 */
type Theme = 'light' | 'dark' | 'system';

/**
 * 主题上下文类型接口
 * @description 定义主题上下文中暴露的状态和方法
 */
interface ThemeContextType {
  theme: Theme;            // 当前主题状态
  setTheme: (theme: Theme) => void;  // 更新主题的方法
}

/**
 * 创建主题上下文
 * @description 初始值为undefined，需要在使用前确保存在提供者
 */
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * 主题提供者组件
 * @description 管理主题状态并将其提供给应用的其他部分
 * @param {object} props - 组件属性
 * @param {React.ReactNode} props.children - 子组件
 * @returns {JSX.Element} 主题上下文提供者
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // 创建主题状态，默认值为'system'（跟随系统）
  const [theme, setTheme] = useState<Theme>('system');

  // 首次加载时从本地存储中恢复用户偏好的主题
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as Theme | null;
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  // 当主题变化时更新DOM和本地存储
  useEffect(() => {
    // 保存用户选择到本地存储
    localStorage.setItem('theme', theme);

    // 根据主题设置添加或移除dark类
    if (theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * 主题钩子
 * @description 提供一个简单的方式来访问和操作主题上下文
 * @returns {ThemeContextType} 包含当前主题和设置主题的方法
 * @throws {Error} 如果在ThemeProvider外部使用，则抛出错误
 */
export function useTheme() {
  // 从上下文中获取主题状态
  const context = useContext(ThemeContext);
  // 验证上下文是否存在
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
